/**
 * Miniatura de landing page — a página de verdade, encolhida.
 *
 * É o mesmo `renderizarPagina` da URL pública dentro de um iframe em escala,
 * não um desenho aproximado: a miniatura que mente sobre a página é pior que
 * nenhuma. Três decisões de segurança/peso:
 *
 *  - `previa: true` + `sandbox=""`: nenhum script roda. Pixel de marketing não
 *    dispara por causa de uma listagem aberta no painel — isso contaminaria a
 *    medição da campanha com visitas internas.
 *  - `pointer-events: none` + `tabIndex={-1}`: a miniatura é imagem, não
 *    página navegável; o clique pertence ao card que a envolve.
 *  - `loading="lazy"`: dez páginas na galeria são dez documentos completos;
 *    só os visíveis custam alguma coisa.
 */

import { renderizarPagina, type PaginaLanding } from '@egba-do/shared'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'

const URL_API = import.meta.env.VITE_API_URL ?? ''

/** Largura "de computador" em que a página é renderizada antes de encolher. */
const LARGURA_BASE = 1280

export function Miniatura({ pagina, altura = 168 }: { pagina: PaginaLanding; altura?: number }) {
  const caixa = useRef<HTMLDivElement>(null)
  const [escala, setEscala] = useState(0.2)

  useLayoutEffect(() => {
    const el = caixa.current
    if (!el) return
    const medir = () => setEscala(Math.max(el.clientWidth / LARGURA_BASE, 0.05))
    medir()
    const ro = new ResizeObserver(medir)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const html = useMemo(() => {
    const doc = renderizarPagina(pagina, { urlApi: URL_API, previa: true })
    // <base> resolve /publico/midia/... na API, não no domínio do painel; o
    // overflow escondido tira a barra de rolagem gigante da escala.
    return doc.replace(
      '</head>',
      `<base href="${URL_API ? `${URL_API}/` : '/'}"><style>html,body{overflow:hidden}</style></head>`,
    )
  }, [pagina])

  return (
    <div
      ref={caixa}
      aria-hidden="true"
      className="pointer-events-none relative overflow-hidden bg-white"
      style={{ height: altura }}
    >
      <iframe
        title=""
        srcDoc={html}
        sandbox=""
        tabIndex={-1}
        loading="lazy"
        className="absolute top-0 left-0 border-0"
        style={{
          width: LARGURA_BASE,
          height: Math.ceil(altura / escala),
          transform: `scale(${escala})`,
          transformOrigin: 'top left',
        }}
      />
      {/* Véu sutil para o texto encolhido não brigar com o card. */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/5" />
    </div>
  )
}
